import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, OverlayTrigger, Row, Stack, Tooltip } from "react-bootstrap";
import Input from "./Input";
import { Task } from "../../types/task";
import SelectFamily from "./SelectFamily";
import SelectObjectTypeahead from "./SelectObjectTypeahead";
import {
  deleteTaskTemplate,
  fetchGoalCategories,
  postGoalCategory,
  postGoalTemplate,
  postTaskTemplate,
  postTask,
  postGoal,
  deleteTask,
} from "../../services/httpClient";
import useApp from "../../utils/useApp";

interface CreateGoalState {
  family?: any;
  goalSaved: boolean;
  setGoalSaved: any;
  templateMode?: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const CreateGoalCard = ({
  family,
  goalSaved,
  setGoalSaved,
  templateMode = false,
  setShowToast,
  setToastText,
  setToastBody,
}: CreateGoalState) => {
  const { auth }: any = useApp();
  const id = auth?.profile?.userOrgID;
  const [goalId, setGoalId] = useState<string>("");
  const [goalName, setGoalName] = useState<string>("");
  const [category, setCategory] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [task, setTask] = useState<string>("");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allowSaveGoal, setAllowSaveGoal] = useState<boolean>(false);
  const [familyUser, setFamilyUser] = useState<string>("unassigned");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    const realFamily = (family || []).filter((member: any) => member.name !== "Unassigned");
    if (realFamily.length === 1) {
      setFamilyUser(realFamily[0].id + "|" + realFamily[0].type);
    }
  }, [family]);

  useEffect(() => {
    async function fetchData() {
      await Promise.all([getGoalCategories()]);
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGoalCategories = async () => {
    await fetchGoalCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setCategories(cats);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const updateFamilyUser = (e: any) => {
    setFamilyUser(e.target.value);
  };

  const updateGoalName = (e: any) => {
    setGoalName(e.target.value);
    setAllowSaveGoal(true);
  };

  const updateCategory = (e: any) => {
    setCategory(e);
  };

  const updateTask = (e: any) => {
    setTask(e.target.value);
  };

  const onClickAddCategory = async (newCategoryName: string) => {
    const newCategory = newCategoryName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    return postGoalCategory({ name: newCategory })
      .then((res: any) => {
        setShowToast(true);
        setToastText("Category Created");
        setToastBody(newCategory);
        getGoalCategories();
        const cat = `${id}_${newCategory.toLowerCase()}`;
        setCategory(cat);
        return { key: cat, name: newCategory, id: res[0].data.category_id };
      })
      .catch((err: any) => {
        console.log(err);
        throw err;
      });
  };

  const onClickGoalTemplateSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveGoalTemplate();
  };

  const onClickSaveGoalTemplate = () => {
    const newGoal = category ? { name: goalName, category_id: category?.id !== "0" ? category?.id : null } : { name: goalName };

    postGoalTemplate(newGoal, auth?.profile?.userOrgID)
      .then((res: any) => {
        setGoalId(res[0].data.goal_template_id);
        setGoalSaved(true);
        setShowToast(true);
        setToastText("Goal Template Created");
        setToastBody(goalName);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickStartOver = () => {
    setGoalName("");
    setCategory(null);
    setTask("");
    setTasks([]);
    setGoalSaved(false);
  };

  const onClickTaskTemplateSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveTaskTemplate();
  };

  const onClickSaveTaskTemplate = () => {
    const newTask = {
      name: task,
    };

    setErrMsg("");
    //loading toast to show adding a task
    setShowToast(true);
    setToastText("Adding Task");
    setToastBody(task);

    postTaskTemplate(newTask, auth?.profile?.userOrgID, goalId)
      .then((res: any) => {
        const newTaskId = {
          id: res[0].data.task_template_id,
          name: task,
        };
        setTasks([...tasks, newTaskId]);
        setShowToast(true);
        setToastText("Task Created");
        setToastBody(task);
        setTask("");
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickRemoveTaskTemplate = (t: Task) => {
    setErrMsg("");
    deleteTaskTemplate(auth?.profile?.userOrgID, goalId, t.id)
      .then((res: any) => {
        setTasks((oldValues) => {
          return oldValues.filter((task) => task.name !== t.name);
        });
        setShowToast(true);
        setToastText("Task Deleted");
        setToastBody(t.name);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  /////
  const onClickGoalSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveGoal();
  };

  const onClickSaveGoal = (goalSubject?: string) => {
    if (!goalName) {
      setErrMsg("Cannot set null goal name");
      return;
    }

    const newGoal: any = {
      name: goalName,
      is_completed: false,
      is_archived: false,
    };

    if (
      goalSubject &&
      goalSubject !== "unassigned" &&
      !goalSubject.includes("Unassigned") &&
      !goalSubject.toLowerCase().includes("unassigned")
    ) {
      const parts = goalSubject.split("|");

      if (parts.length === 2 && parts[0] && parts[1] && parts[0] !== "Unassigned" && parts[1] !== "Unassigned") {
        newGoal.subject = { id: parts[0], type: parts[1] };
      }
    }

    setErrMsg("");

    postGoal(newGoal, id)
      .then((res: any) => {
        setGoalId(res[0].data.goal_id);
        setGoalSaved(true);
        setShowToast(true);
        setToastText("Goal Created");
        setToastBody(goalName);
        setTasks([]);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickTaskSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveTask();
  };

  const onClickSaveTask = () => {
    if (!task) {
      setErrMsg("Cannot set null task name");
      return;
    }
    const newTask = {
      name: task,
      is_next: false,
      is_completed: false,
      is_cm_reviewed: false,
      is_archived: false,
    };

    setErrMsg("");

    postTask(newTask, id, goalId)
      .then((res: any) => {
        const newTaskId = {
          id: res[0].data.task_id,
          name: task,
          is_next: false,
          is_completed: false,
          is_cm_reviewed: false,
          is_archived: false,
        };
        setTasks([...tasks, newTaskId]);
        setShowToast(true);
        setToastText("Task Created");
        setToastBody(task);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickRemoveTask = (t: Task) => {
    setErrMsg("");
    deleteTask(id, goalId, t.id)
      .then((res: any) => {
        setTasks((oldValues) => {
          return oldValues.filter((task) => task.name !== t.name);
        });
        setShowToast(true);
        setToastText("Task Deleted");
        setToastBody(t.name);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  return (
    <>
      <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
        {errMsg}
      </p>
      <Card className="bg-light px-3 pt-3">
        {family && (
          <Row className="mb-3">
            <SelectFamily
              family={family}
              familyUser={familyUser}
              updateFamilyUser={updateFamilyUser}
              readOnly={goalSaved}
              skipDefaultSelection={true}
            />
          </Row>
        )}
        <Form onSubmit={(e: any) => (templateMode ? onClickGoalTemplateSubmit(e) : onClickGoalSubmit(e))}>
          <Row>
            <Col xs={12} lg={templateMode && goalSaved ? 8 : templateMode && !goalSaved ? 7 : 12} className="mb-3">
              <Input
                label="Enter New Goal"
                type="textarea"
                value={goalName}
                onUpdate={(e: any) => updateGoalName(e)}
                disabled={goalSaved ? true : false}
                hasFocus={true}
              />
            </Col>
            {templateMode && (
              <Col xs={12} lg={templateMode ? 4 : 12} className="mb-3">
                <SelectObjectTypeahead
                  initialOption="Search or type to create"
                  label="Category"
                  value={category}
                  options={categories}
                  onUpdate={(e: any) => updateCategory(e)}
                  allowCreate={true}
                  onCreateOption={async (inputValue: string) => {
                    const newCategory = await onClickAddCategory(inputValue);
                    return newCategory;
                  }}
                  disabled={goalSaved ? true : false}
                />
              </Col>
            )}
            {!goalSaved && (
              <Col xs={12} lg={templateMode ? 1 : 12} className="align-items-center d-flex mb-3">
                <Button
                  className="button app-secondary-color button"
                  onClick={() => (templateMode ? onClickSaveGoalTemplate() : onClickSaveGoal())}
                  disabled={!allowSaveGoal}
                >
                  Save
                </Button>
              </Col>
            )}
          </Row>
        </Form>
        {goalSaved && (
          <>
            <Row>
              <h6>Tasks</h6>
            </Row>
            {tasks.length > 0 && (
              <Row className="mb-2">
                <Col xs={12}>
                  {tasks.map((t: Task) => (
                    <Stack direction="horizontal" gap={3}>
                      <Col>
                        <Form.Control type="text" value={t.name} readOnly />
                      </Col>
                      <Col xs={2} lg={1}>
                        <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={<Tooltip>Delete Task</Tooltip>}>
                          <FontAwesomeIcon
                            className="text-muted cursor-pointer"
                            icon={faTrash}
                            onClick={(e) => (templateMode ? onClickRemoveTaskTemplate(t) : onClickRemoveTask(t))}
                          />
                        </OverlayTrigger>
                      </Col>
                    </Stack>
                  ))}
                </Col>
              </Row>
            )}
            <Form onSubmit={(e: any) => (templateMode ? onClickTaskTemplateSubmit(e) : onClickTaskSubmit(e))}>
              <Row>
                <Col xs={12} className="mb-3">
                  <Stack direction="horizontal" gap={3}>
                    <Col>
                      <Input label="Enter New Task" type="text" value={task} hasFocus={true} onUpdate={(e: any) => updateTask(e)} />
                    </Col>
                    <Col xs={2} lg={1}>
                      <Button
                        className="button app-secondary-color"
                        onClick={() => (templateMode ? onClickSaveTaskTemplate() : onClickSaveTask())}
                        disabled={!allowSaveGoal}
                      >
                        Save
                      </Button>
                    </Col>
                  </Stack>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Card>
      {goalSaved && (
        <Row className="mt-3">
          <Col className="text-center">
            <Button className="button app-primary-color" onClick={() => onClickStartOver()}>
              Create Another Goal
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default CreateGoalCard;
